import React, { useState, useEffect } from 'react';
import { format, parseISO, startOfDay, setHours, setMinutes } from 'date-fns';
import { de } from 'date-fns/locale';
import localforage from 'localforage';

const UserHistoryView = ({ user, onClose }) => {
  const [groupedHistory, setGroupedHistory] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDays, setExpandedDays] = useState([]);
  const [animationIndex, setAnimationIndex] = useState(0);
  const isMobile = window.innerWidth <= 768;

  const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';

  useEffect(() => {
    // Automatischer Reset um 00:10
    const checkAndResetStatus = async () => {
      const now = new Date();
      if (now.getHours() === 8 && now.getMinutes() === 11) {
        try {
          const token = await localforage.getItem('token');
          await fetch(`${BASE_URL}/api/auth/reset-status`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email: user.email })
          });
        } catch (error) {
          console.error('Reset Fehler:', error);
        }
      }
    };

    const interval = setInterval(checkAndResetStatus, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    fetchAndProcessHistory();
    // Starte Animation für Einträge
    const timer = setTimeout(() => {
      setAnimationIndex(1);
    }, 100);
    return () => clearTimeout(timer);
  }, [user]);

  const fetchAndProcessHistory = async () => {
    try {
      setIsLoading(true);
      const token = await localforage.getItem('token');
      const response = await fetch(
        `${BASE_URL}/api/auth/status-history/email/${user.email}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Fehler beim Laden der Historie');
      
      const data = await response.json();
      processHistoryData(data.history);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const processHistoryData = (history) => {
    const grouped = history.reduce((acc, entry) => {
      const date = startOfDay(parseISO(entry.timestamp));
      const dateKey = format(date, 'yyyy-MM-dd');
      
      if (!acc[dateKey]) {
        acc[dateKey] = {
          date,
          entries: [],
          endOfDayStatus: null
        };
      }
      
      acc[dateKey].entries.push({
        ...entry,
        timestamp: parseISO(entry.timestamp)
      });
      
      if (!acc[dateKey].endOfDayStatus || 
          entry.timestamp > acc[dateKey].entries[acc[dateKey].entries.length - 1].timestamp) {
        acc[dateKey].endOfDayStatus = entry.newStatus;
      }
      
      return acc;
    }, {});

    Object.values(grouped).forEach(day => {
      day.entries.sort((a, b) => b.timestamp - a.timestamp);
    });

    setGroupedHistory(grouped);
  };

  const toggleDay = (dateKey) => {
    setExpandedDays(prev =>
      prev.includes(dateKey)
        ? prev.filter(key => key !== dateKey)
        : [...prev, dateKey]
    );
  };

  const getStatusBadgeStyle = (status, isEndOfDay = false) => ({
    ...styles.statusBadge,
    backgroundColor: status === 'CHECKEDOUT' 
      ? 'rgba(6, 95, 70, 0.95)'
      : 'rgba(153, 27, 27, 0.95)',
    transform: isEndOfDay ? 'scale(1.05)' : 'scale(1)',
    boxShadow: isEndOfDay 
      ? '0 4px 12px rgba(0, 0, 0, 0.15)' 
      : '0 2px 6px rgba(0, 0, 0, 0.1)'
  });

  if (isLoading) {
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.loadingContainer}>
          <div style={styles.pulsingDot} />
          <p style={styles.loadingText}>Lade Historie...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error}</p>
          <button onClick={onClose} style={styles.closeButton}>
            Schließen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={{
        ...styles.modalContent,
        opacity: animationIndex ? 1 : 0,
        transform: animationIndex 
          ? 'translateY(0) scale(1)' 
          : 'translateY(20px) scale(0.95)'
      }}>
        <div style={styles.modalHeader}>
          <h2 style={styles.headerTitle}>Status Historie - {user.username}</h2>
          <button 
            onClick={onClose} 
            style={styles.closeButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'rotate(90deg) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'rotate(0deg) scale(1)';
            }}
          >
            ×
          </button>
        </div>

        <div style={styles.modalBody}>
          {Object.entries(groupedHistory)
            .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
            .map(([dateKey, dayData], index) => {
              const isExpanded = expandedDays.includes(dateKey);

              return (
                <div 
                  key={dateKey} 
                  style={{
                    ...styles.dayContainer,
                    opacity: animationIndex ? 1 : 0,
                    transform: animationIndex 
                      ? 'translateX(0)' 
                      : 'translateX(-20px)',
                    transition: `all 0.3s ease ${index * 0.1}s`
                  }}
                >
                  <div
                    onClick={() => toggleDay(dateKey)}
                    style={{
                      ...styles.dayHeader,
                      backgroundColor: isExpanded ? '#f8fafc' : '#ffffff'
                    }}
                  >
                    <h3 style={styles.dayTitle}>
                      {format(dayData.date, 'EEEE, dd. MMMM yyyy', { locale: de })}
                    </h3>
                    <div style={styles.endOfDayStatus}>
                      <span style={styles.endOfDayLabel}>
                        Status:
                      </span>
                      <span style={getStatusBadgeStyle(dayData.endOfDayStatus, true)}>
                        {dayData.endOfDayStatus === 'CHECKEDOUT' 
                          ? 'Abgemeldet' 
                          : 'Nicht Abgemeldet'}
                      </span>
                      <span 
                        style={{
                          ...styles.expandIcon,
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                      >
                        ▼
                      </span>
                    </div>
                  </div>

                  <div style={{
                    ...styles.collapsibleContent,
                    maxHeight: isExpanded ? '400px' : '0',
                    opacity: isExpanded ? 1 : 0,
                    transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)'
                  }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          {!isMobile && (
                            <th style={styles.th}>Uhrzeit</th>
                          )}
                          <th style={styles.th}>Status</th>
                          <th style={styles.th}>Geändert von</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayData.entries.map((entry, entryIndex) => (
                          <tr 
                            key={entryIndex} 
                            style={{
                              ...styles.tr,
                              animation: isExpanded 
                                ? `fadeIn 0.3s ease ${entryIndex * 0.05}s forwards`
                                : 'none',
                              opacity: 0
                            }}
                          >
                            {!isMobile && (
                              <td style={styles.td}>
                                {format(entry.timestamp, 'HH:mm', { locale: de })}
                              </td>
                            )}
                            <td style={styles.td}>
                              <span style={getStatusBadgeStyle(entry.newStatus)}>
                                {entry.newStatus === 'CHECKEDOUT' 
                                  ? 'Abgemeldet' 
                                  : 'Nicht Abgemeldet'}
                              </span>
                            </td>
                            <td style={styles.td}>{entry.updatedBy}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    animation: 'modalFadeIn 0.3s ease-out'
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '92%',
    maxWidth: '1100px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    transition: 'all 0.4s ease-out'
  },
  modalHeader: {
    padding: '20px 24px',
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  },
  headerTitle: {
    color: '#ffffff',
    margin: 0,
    fontSize: '22px',
    fontWeight: '600',
    letterSpacing: '-0.5px'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ffffff',
    fontSize: '28px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    outline: 'none'
  },
  modalBody: {
    padding: '24px',
    backgroundColor: '#f8fafc',
    overflowY: 'auto',
    flex: 1
  },
  dayContainer: {
    marginBottom: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  },
  dayHeader: {
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    ':hover': {
      backgroundColor: '#f8fafc'
    }
  },
  dayTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b'
  },
  endOfDayStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  endOfDayLabel: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500'
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    display: 'inline-block',
    transition: 'all 0.3s ease',
    letterSpacing: '0.3px'
  },
  expandIcon: {
    fontSize: '12px',
    color: '#64748b',
    transition: 'transform 0.3s ease',
    marginLeft: '8px'
  },
  collapsibleContent: {
    transition: 'all 0.3s ease-in-out',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
    backgroundColor: '#ffffff'
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tr: {
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#f8fafc'
    }
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: '#475569',
    borderBottom: '1px solid #e2e8f0',
    transition: 'all 0.2s ease'
  },
  loadingContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    animation: 'slideUp 0.3s ease-out'
  },
  loadingText: {
    fontSize: '16px',
    color: '#475569',
    fontWeight: '500',
    margin: 0
  },
  pulsingDot: {
    width: '20px',
    height: '20px',
    backgroundColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'pulse 1.5s infinite'
  },
  errorContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    animation: 'slideUp 0.3s ease-out'
  },
  errorText: {
    color: '#ef4444',
    fontSize: '16px',
    marginBottom: '24px',
    fontWeight: '500'
  },
  '@keyframes modalFadeIn': {
    from: {
      opacity: 0,
      backdropFilter: 'blur(0)'
    },
    to: {
      opacity: 1,
      backdropFilter: 'blur(5px)'
    }
  },
  '@keyframes slideUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.7)'
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 10px rgba(59, 130, 246, 0)'
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(59, 130, 246, 0)'
    }
  },
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(5px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
};
export default UserHistoryView;
;